import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Field, reduxForm } from 'redux-form';

import { userPropTypes } from 'helpers/propTypes';

import Input from 'components/commons/Input';
import Button from 'components/commons/Button';

import mercadoPagoImage from 'views/Checkout/assets/mercado-pago.png';
import compraSeguraImage from 'views/Checkout/assets/compra-segura.png';

import './PickupForm.scss';

const validate = (values) => {
  const errors = {};
  if (!values.first_name_retire) {
    errors.first_name_retire = 'Nombre inválido';
  }
  if (!values.last_name_retire) {
    errors.last_name_retire = 'Apellido inválido';
  }
  if (!values.id_card_retire || !/^[0-9]*$/i.test(values.id_card_retire)) {
    errors.id_card_retire = 'Número inválido';
  }
  if (!values.email_retire || !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(values.email_retire)) {
    errors.email_retire = 'Email inválido';
  }
  if (!values.phone_retire || !/^(54)?[1-9]\d{9}$/i.test(values.phone_retire)) {
    errors.phone_retire = 'Número inválido (Ej: 541123456789)';
  }
  return errors;
};

const PickupForm = (props) => {
  const {
    handleSubmit, initialize, onSubmit, submitting, user,
    couponText, setCouponText, onSubmitCoupon, coupon, emailComplete,
    setEmailComplete, validationEmailCoupon, setEmailCoupon, onSecureCheckoutClick,
    showMPButton, hayAcuerdoConVendedor,
  } = props;

  useEffect(() => {
    if (user) {
      initialize({
        first_name_retire: user.first_name,
        last_name_retire: user.last_name,
        email_retire: user.email,
        phone_retire: user.phone,
      });
      setEmailComplete(true);
      setEmailCoupon(user.email);
    }
    // eslint-disable-next-line
  }, [user, setEmailComplete, setEmailCoupon]);

  const onKeyPress = (event) => {
    if (event.key === 'Enter') {
      onSubmitCoupon();
    }
  };

  return (
    <div className="container checkout-step checkout-step--pickup" data-testid="pickup-form">
      <div className="row">
        <div className="form-container">
          <div className="checkout-section-header">
            <div>
              <span className="checkout-section-kicker">Retiro</span>
              <h3 className="checkout-section-title">Retiro en sucursal</h3>
              <div className="checkout-section-subtitle">
                Dejanos los datos de la persona que va a retirar el pedido.
              </div>
            </div>
            <div className="secure-badge" aria-label="Compra segura">
              <img className="secure-badge__image" src={compraSeguraImage} alt="Compra segura" />
              <div className="secure-badge__text">
                <div className="secure-badge__title">Compra segura</div>
                <div className="secure-badge__subtitle">Datos protegidos</div>
              </div>
            </div>
          </div>

          <form className="redux-form checkout-form-grid" encType="multipart/form-data" onSubmit={handleSubmit(onSubmit)}>
            <Field component={Input} label="Nombre" name="first_name_retire" />
            <Field component={Input} label="Apellido" name="last_name_retire" />
            <Field
              component={Input}
              label={process.env.REACT_APP_CLIENT_ID === '5' ? 'CUIT' : 'DNI'}
              name="id_card_retire"
              type="number"
            />
            <Field
              component={Input}
              label="Email"
              disabled={!!user}
              name="email_retire"
              onChange={(event) => validationEmailCoupon(event.target.value)}
              type="email"
            />
            <Field component={Input} label="Celular / WhatsApp" name="phone_retire" type="number" />
            <div className="checkout-form-full">
              <Field component={Input} label="Comentarios para el vendedor" isTextArea name="comments" />
            </div>

            <div className="checkout-coupon-card checkout-form-full">
              <div>
                <strong>Cupón de descuento</strong>
                <small>Ingresá tu email para poder validar el cupón.</small>
              </div>
              <div className="checkout-coupon-row">
                <Field
                  component={Input}
                  label="Aplicar cupón"
                  name="coupon"
                  disabled={!!coupon || !emailComplete}
                  value={couponText}
                  onChange={(event) => setCouponText(event.target.value)}
                  onKeyPress={onKeyPress}
                />
                <Button
                  buttonClass={!coupon ? 'btn-search-set checkout-coupon-button' : 'btn-search-reset checkout-coupon-button'}
                  disabled={!emailComplete}
                  text={!coupon ? 'Aplicar' : <i className="fa fa-trash-o" aria-hidden="true" />}
                  type="button"
                  onClick={onSubmitCoupon}
                />
              </div>
            </div>

            <div className="checkout-form-full checkout-checkbox-line">
              <Field
                component={Input}
                label="Deseo recibir novedades y promociones"
                name="suscribe"
                type="checkbox"
              />
            </div>

            <div className="checkout-pay-actions checkout-form-full">
              {!!hayAcuerdoConVendedor && (
                <Button
                  text="Arreglar pago con el vendedor"
                  type="button"
                  onClick={handleSubmit(onSubmit)}
                  buttonClass="btn-block checkout-secondary-action"
                  disabled={submitting}
                />
              )}
              {showMPButton && (
                <Button
                  text={(
                    <div className="secure-checkout-button-content justify-content-center">
                      <img src={mercadoPagoImage} alt="Mercado Pago" height="18" />
                      <span>Pagá con tarjeta de crédito o débito</span>
                    </div>
                  )}
                  type="button"
                  onClick={handleSubmit(onSecureCheckoutClick)}
                  buttonClass="btn-block secure-checkout-button"
                  disabled={submitting}
                />
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

PickupForm.defaultProps = {
  user: null,
  coupon: null,
  couponText: '',
  emailComplete: false,
  hayAcuerdoConVendedor: false,
};

PickupForm.propTypes = {
  coupon: PropTypes.shape({
    id: PropTypes.number.isRequired,
    percentage: PropTypes.number.isRequired,
  }),
  handleSubmit: PropTypes.func.isRequired,
  initialize: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  submitting: PropTypes.bool.isRequired,
  user: userPropTypes,
  couponText: PropTypes.string,
  setCouponText: PropTypes.func.isRequired,
  emailComplete: PropTypes.bool,
  onSubmitCoupon: PropTypes.func.isRequired,
  setEmailComplete: PropTypes.func.isRequired,
  setEmailCoupon: PropTypes.func.isRequired,
  validationEmailCoupon: PropTypes.func.isRequired,
  onSecureCheckoutClick: PropTypes.func.isRequired,
  showMPButton: PropTypes.bool.isRequired,
  hayAcuerdoConVendedor: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
};

export default reduxForm({
  form: 'pickup',
  validate,
})(PickupForm);
